import { Component, OnInit, signal, computed, effect, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AuthService } from '../../services/auth.service';
import { Login } from "../login/login";
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-initial',
  standalone: true,
  imports: [Login, CommonModule],
  templateUrl: './initial.html',
  styleUrl: './initial.css'
})
export class Initial implements OnInit {
  // Dependency injection using inject() function (Angular 14+)
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly destroyRef = inject(DestroyRef);

  // Using signals for reactive state management (Angular 16+)
  public gsid = signal<number>(0);
  public showContent = signal<boolean>(false);
  public isLoading = signal<boolean>(false);
  public error = signal<string | null>(null);

  // Computed signals for derived state
  public hasValidGsid = computed(() => this.gsid() > 0);
  public isReady = computed(() => this.showContent() && !this.isLoading());

  constructor() {
    // Effect to react to gsid changes (Angular 16+)
    effect(() => {
      const currentGsid = this.gsid();
      if (currentGsid > 0) {
        console.log('GSID changed to:', currentGsid);
      }
    }, { allowSignalWrites: false });
  }

  ngOnInit() {
    this.initializeComponent();
  }

  private initializeComponent(): void {
    // Using takeUntilDestroyed for automatic unsubscription (Angular 16+)
    this.route.params
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (params) => {
          const id = Number(params['id']);
          this.gsid.set(id);
          
          if (this.hasValidGsid()) {
            this.getGuestAccess();
          } else {
            this.error.set('Invalid GSID provided');
            this.router.navigate(['/']);
          }
        },
        error: (error) => {
          console.error('Error reading route params:', error);
          this.error.set('Failed to read route parameters');
        }
      });
  }

  private getGuestAccess(): void {
    console.log("1 ==> Getting guest access for gsid:", this.gsid());
    this.isLoading.set(true);
    this.error.set(null);

    this.authService.getGuestAccess()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.isLoading.set(false);
          
          if (response?.data?.token) {
            this.authService.saveWithOutAuthData(
              response.data.token, 
              response.data.uid, 
              this.gsid()
            );
            this.showContent.set(true);
          } else {
            this.error.set('No token received from server');
            this.router.navigate(['/']);
          }
        },
        error: (error) => {
          console.error('Error getting guest access:', error);
          this.isLoading.set(false);
          this.error.set('Failed to authenticate as guest');
          this.router.navigate(['/']);
        }
      });
  }

  // Public method to retry authentication
  public retry(): void {
    if (this.hasValidGsid()) {
      this.getGuestAccess();
    }
  }
}
